/**
 * The console's API client.
 *
 * One thin, typed fetch wrapper over the *generated* contract types (`schema.gen.ts`,
 * produced from the committed `openapi.json`). Every endpoint the console uses appears
 * once in `API_SURFACE`, and `client.test.ts` compares that list against the committed
 * spec — an endpoint added upstream without a client stops the build rather than
 * showing up as a 404 in front of a customer.
 *
 * Mocking: all requests are relative to the current origin (`VITE_API_BASE_URL` may
 * override), so MSW's service worker intercepts them and the console runs with zero
 * backend.
 */
import type {
  AdAccountPage,
  Blueprint,
  BlueprintDiff,
  BlueprintEditRequest,
  BlueprintSummary,
  ErrorResponse,
  GenerateRequest,
  IngestRequest,
  JobAccepted,
  JobStatus,
  KeywordIdeaPage,
  LogoutResponse,
  MeResponse,
  MetricPage,
  OAuthStartRequest,
  OAuthStartResponse,
  OpsBudgetStatus,
  OpsEstimate,
  Platform,
  PreflightReport,
  ProjectCreateRequest,
  ProjectSummary,
  PushRequest,
  PushResult,
  PushStatusResponse,
  RefreshRequest,
  RefreshResponse,
  ResearchRequest,
  ReverseRequest,
  RevocationResponse,
  ValidationReport,
} from './types'

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''

export class ApiRequestError extends Error {
  readonly status: number
  readonly code: string
  readonly fieldPath: string | null
  readonly body: ErrorResponse | null

  constructor(status: number, body: ErrorResponse | null, fallback: string) {
    super(body?.detail ?? fallback)
    this.name = 'ApiRequestError'
    this.status = status
    this.code = body?.code ?? `HTTP_${status}`
    this.fieldPath = body?.field_path ?? null
    this.body = body
  }
}

type Query = Record<string, string | number | boolean | undefined | null>

function withQuery(path: string, query?: Query): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs === '' ? path : `${path}?${qs}`
}

interface RequestOptions {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  query?: Query
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
}

/**
 * The spec declares `HTTPBearer` on the session, credential and OAuth-start operations.
 * The console has no token store of its own (there is no sign-in screen in this wave), so
 * the caller passes the access token it holds and no header is sent when it has none.
 */
function bearer(accessToken?: string): Record<string, string> | undefined {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined
}

interface ApiResponse<T> {
  data: T
  response: Response
}

async function request<T>(path: string, options: RequestOptions): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  const init: RequestInit = { method: options.method, headers }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(options.body)
  }
  if (options.signal) init.signal = options.signal

  const response = await fetch(`${API_BASE_URL}${withQuery(path, options.query)}`, init)

  if (!response.ok) {
    let body: ErrorResponse | null = null
    try {
      const parsed = (await response.json()) as ErrorResponse
      if (parsed && typeof parsed.code === 'string') body = parsed
    } catch {
      body = null
    }
    throw new ApiRequestError(
      response.status,
      body,
      `Request failed: ${options.method} ${path} (${response.status})`,
    )
  }

  const text = await response.text()
  const data = (text === '' ? undefined : JSON.parse(text)) as T
  return { data, response }
}

async function call<T>(path: string, options: RequestOptions): Promise<T> {
  const { data } = await request<T>(path, options)
  return data
}

/**
 * Every operation the committed spec declares — the spec's size lives in `contract.json`
 * and is read from there, never from a comment. `id` maps 1:1 to the exported function in
 * `api` below; `client.test.ts` asserts the set of `method + path` pairs equals the
 * spec's exactly, and that every id here has a function, so an endpoint added upstream
 * without a typed method stops `test`, `build` and `e2e` rather than 404ing in front of a
 * customer.
 */
export const API_SURFACE = [
  // --- Session, credentials and OAuth (Wave 2 surface) ----------------------
  { id: 'getMe', method: 'GET', path: '/api/v1/auth/me' },
  { id: 'refreshSession', method: 'POST', path: '/api/v1/auth/session/refresh' },
  { id: 'logout', method: 'POST', path: '/api/v1/auth/session/logout' },
  { id: 'revokeCredential', method: 'DELETE', path: '/api/v1/auth/credentials/{credential_id}' },
  { id: 'startOAuth', method: 'POST', path: '/api/v1/oauth/{platform}/start' },
  { id: 'oauthCallback', method: 'GET', path: '/api/v1/oauth/{platform}/callback' },
  // --- Product surface ------------------------------------------------------
  { id: 'listAdAccounts', method: 'GET', path: '/api/v1/ad-accounts' },
  { id: 'createProject', method: 'POST', path: '/api/v1/projects' },
  { id: 'getProject', method: 'GET', path: '/api/v1/projects/{project_id}' },
  { id: 'listBlueprints', method: 'GET', path: '/api/v1/projects/{project_id}/blueprints' },
  { id: 'generateBlueprint', method: 'POST', path: '/api/v1/projects/{project_id}/blueprints' },
  { id: 'ingestSite', method: 'POST', path: '/api/v1/projects/{project_id}/ingest' },
  { id: 'startResearch', method: 'POST', path: '/api/v1/projects/{project_id}/research' },
  { id: 'listKeywordIdeas', method: 'GET', path: '/api/v1/projects/{project_id}/keyword-ideas' },
  { id: 'getMetrics', method: 'GET', path: '/api/v1/projects/{project_id}/metrics' },
  { id: 'getBlueprint', method: 'GET', path: '/api/v1/blueprints/{blueprint_id}' },
  { id: 'editBlueprint', method: 'PATCH', path: '/api/v1/blueprints/{blueprint_id}' },
  { id: 'diffBlueprint', method: 'GET', path: '/api/v1/blueprints/{blueprint_id}/diff' },
  { id: 'dryRun', method: 'POST', path: '/api/v1/blueprints/{blueprint_id}/dry-run' },
  { id: 'opsEstimate', method: 'POST', path: '/api/v1/blueprints/{blueprint_id}/ops-estimate' },
  { id: 'preflight', method: 'POST', path: '/api/v1/blueprints/{blueprint_id}/preflight' },
  { id: 'pushBlueprint', method: 'POST', path: '/api/v1/blueprints/{blueprint_id}/push' },
  { id: 'getJob', method: 'GET', path: '/api/v1/jobs/{job_id}' },
  { id: 'getOpsBudget', method: 'GET', path: '/api/v1/ops-budget' },
  { id: 'getPush', method: 'GET', path: '/api/v1/pushes/{push_id}' },
  { id: 'reversePush', method: 'POST', path: '/api/v1/pushes/{push_id}/reverse' },
  { id: 'healthz', method: 'GET', path: '/healthz' },
  // Readiness is a different question from liveness and the console's ops view needs both:
  // /healthz says the process is up, /readyz says the ops-budget gate it will lease against
  // is configured for the access level that was granted (findings F1/F2).
  { id: 'readyz', method: 'GET', path: '/readyz' },
] as const

export type ApiSurfaceId = (typeof API_SURFACE)[number]['id']

const enc = encodeURIComponent

export const api = {
  healthz: (signal?: AbortSignal) => request<undefined>('/healthz', { method: 'GET', signal }),

  /**
   * Readiness: the ops-budget gate this deployment will lease against is configured for the
   * access level that was granted, and Postgres answers. Answers 503 with `ErrorResponse`
   * (code `NOT_READY`) when it is not — a probe, not a resource, so the body is the probe's
   * own report rather than a frozen schema.
   */
  readyz: (signal?: AbortSignal) =>
    request<{ status: string; ops_budget?: Record<string, string>; lease_ttl_seconds?: number }>(
      '/readyz',
      { method: 'GET', signal },
    ),

  /** The authenticated user, their workspace and effective permissions. */
  getMe: (accessToken?: string, signal?: AbortSignal) =>
    call<MeResponse>('/api/v1/auth/me', { method: 'GET', headers: bearer(accessToken), signal }),

  /**
   * Rotating refresh: the presented token dies and a new pair is issued. Presenting an
   * already-retired token revokes the whole session family (that is replay detection), so
   * the caller must replace its stored pair with this response and never retry the old one.
   */
  refreshSession: (body: RefreshRequest, signal?: AbortSignal) =>
    call<RefreshResponse>('/api/v1/auth/session/refresh', { method: 'POST', body, signal }),

  /**
   * Revokes the whole session family, not just this token: a logout in one tab also kills
   * the rotated tokens other tabs hold. `sessions_revoked` says how many died.
   */
  logout: (accessToken?: string, signal?: AbortSignal) =>
    call<LogoutResponse>('/api/v1/auth/session/logout', {
      method: 'POST',
      headers: bearer(accessToken),
      signal,
    }),

  /**
   * Owner-only: marks a credential revoked and destroys its token material. The response
   * carries the id, not any secret.
   */
  revokeCredential: (credentialId: string, accessToken?: string, signal?: AbortSignal) =>
    call<RevocationResponse>(`/api/v1/auth/credentials/${enc(credentialId)}`, {
      method: 'DELETE',
      headers: bearer(accessToken),
      signal,
    }),

  /**
   * Begins a consent flow and returns the provider's authorization URL plus the signed,
   * single-use `state`. `purpose: 'signin'` names the workspace in the body; `purpose:
   * 'connect'` attaches a grant to the caller's workspace and needs a bearer token.
   */
  startOAuth: (
    platform: Platform,
    body: OAuthStartRequest,
    accessToken?: string,
    signal?: AbortSignal,
  ) =>
    call<OAuthStartResponse>(`/api/v1/oauth/${enc(platform)}/start`, {
      method: 'POST',
      body,
      headers: bearer(accessToken),
      signal,
    }),

  /**
   * The provider redirects the *browser* here, so in a real flow the console lands on this
   * URL rather than calling it from an XHR; the method exists so the surface is complete and
   * a caller that does drive it in-app is typed. The committed spec declares `code`, `state`
   * and `error` as nullable query parameters and **no 200 model** for this operation
   * (`application/json: unknown`) — the outcome of a sign-in arrives as the redirect, not as
   * a payload — so the return type is honestly `unknown` rather than a guessed shape.
   */
  oauthCallback: (
    platform: Platform,
    query: { code?: string | null; state?: string | null; error?: string | null } = {},
    signal?: AbortSignal,
  ) => call<unknown>(`/api/v1/oauth/${enc(platform)}/callback`, { method: 'GET', query, signal }),

  listAdAccounts: (platform?: Platform, signal?: AbortSignal) =>
    call<AdAccountPage>('/api/v1/ad-accounts', { method: 'GET', query: { platform }, signal }),

  getOpsBudget: (signal?: AbortSignal) =>
    call<OpsBudgetStatus[]>('/api/v1/ops-budget', { method: 'GET', signal }),

  createProject: (body: ProjectCreateRequest, signal?: AbortSignal) =>
    call<ProjectSummary>('/api/v1/projects', { method: 'POST', body, signal }),

  getProject: (projectId: string, signal?: AbortSignal) =>
    call<ProjectSummary>(`/api/v1/projects/${enc(projectId)}`, { method: 'GET', signal }),

  listBlueprints: (projectId: string, signal?: AbortSignal) =>
    call<BlueprintSummary[]>(`/api/v1/projects/${enc(projectId)}/blueprints`, {
      method: 'GET',
      signal,
    }),

  generateBlueprint: (
    projectId: string,
    body: GenerateRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ) =>
    call<JobAccepted>(`/api/v1/projects/${enc(projectId)}/blueprints`, {
      method: 'POST',
      body,
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      signal,
    }),

  ingestSite: (
    projectId: string,
    body: IngestRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ) =>
    call<JobAccepted>(`/api/v1/projects/${enc(projectId)}/ingest`, {
      method: 'POST',
      body,
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      signal,
    }),

  startResearch: (projectId: string, body: ResearchRequest, signal?: AbortSignal) =>
    call<JobAccepted>(`/api/v1/projects/${enc(projectId)}/research`, {
      method: 'POST',
      body,
      signal,
    }),

  listKeywordIdeas: (projectId: string, signal?: AbortSignal) =>
    call<KeywordIdeaPage>(`/api/v1/projects/${enc(projectId)}/keyword-ideas`, {
      method: 'GET',
      signal,
    }),

  getMetrics: (
    projectId: string,
    query: { start_date: string; end_date: string; level?: 'campaign' | 'ad_group' | 'keyword' },
    signal?: AbortSignal,
  ) =>
    call<MetricPage>(`/api/v1/projects/${enc(projectId)}/metrics`, {
      method: 'GET',
      query,
      signal,
    }),

  getJob: (jobId: string, signal?: AbortSignal) =>
    call<JobStatus>(`/api/v1/jobs/${enc(jobId)}`, { method: 'GET', signal }),

  getBlueprint: (blueprintId: string, signal?: AbortSignal) =>
    call<Blueprint>(`/api/v1/blueprints/${enc(blueprintId)}`, { method: 'GET', signal }),

  editBlueprint: (
    blueprintId: string,
    body: BlueprintEditRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ) =>
    call<BlueprintSummary>(`/api/v1/blueprints/${enc(blueprintId)}`, {
      method: 'PATCH',
      body,
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      signal,
    }),

  diffBlueprint: (blueprintId: string, against?: string, signal?: AbortSignal) =>
    call<BlueprintDiff>(`/api/v1/blueprints/${enc(blueprintId)}/diff`, {
      method: 'GET',
      query: { against },
      signal,
    }),

  dryRun: (blueprintId: string, adAccountId: string, signal?: AbortSignal) =>
    call<ValidationReport>(`/api/v1/blueprints/${enc(blueprintId)}/dry-run`, {
      method: 'POST',
      query: { ad_account_id: adAccountId },
      signal,
    }),

  opsEstimate: (blueprintId: string, platform: Platform, signal?: AbortSignal) =>
    call<OpsEstimate>(`/api/v1/blueprints/${enc(blueprintId)}/ops-estimate`, {
      method: 'POST',
      body: { platform },
      signal,
    }),

  preflight: (blueprintId: string, adAccountId: string, signal?: AbortSignal) =>
    call<PreflightReport>(`/api/v1/blueprints/${enc(blueprintId)}/preflight`, {
      method: 'POST',
      query: { ad_account_id: adAccountId },
      signal,
    }),

  /**
   * Push is a 202. `PushResult` carries no id, so the console takes the push id from
   * the `Location` header when the API sends one and otherwise falls back to the
   * idempotency key it generated, which the API echoes back in the result.
   * See the contract note in the W1-F report.
   */
  pushBlueprint: async (
    blueprintId: string,
    body: PushRequest,
    signal?: AbortSignal,
  ): Promise<{ result: PushResult; pushId: string | null }> => {
    const { data, response } = await request<PushResult>(
      `/api/v1/blueprints/${enc(blueprintId)}/push`,
      { method: 'POST', body, signal },
    )
    const location = response.headers.get('Location')
    const fromLocation = location ? location.split('/').filter(Boolean).pop() ?? null : null
    return {
      result: data,
      pushId: fromLocation ?? data.idempotency_key ?? body.idempotency_key ?? null,
    }
  },

  getPush: (pushId: string, signal?: AbortSignal) =>
    call<PushStatusResponse>(`/api/v1/pushes/${enc(pushId)}`, { method: 'GET', signal }),

  reversePush: (pushId: string, body: ReverseRequest, signal?: AbortSignal) =>
    call<PushResult>(`/api/v1/pushes/${enc(pushId)}/reverse`, { method: 'POST', body, signal }),
}

/**
 * Deliberately-unused spec operations. Empty, and asserted empty: the console types every
 * operation the committed spec declares. The list exists so that an operation the console
 * genuinely does not call has one explicit, reviewed place to live instead of quietly going
 * missing from `API_SURFACE` — see `client.test.ts`.
 */
export const UNUSED_SPEC_PATHS: ReadonlyArray<string> = []
